import React, { useState, useEffect } from 'react';
import { trackError, trackWarning, trackPerformanceIssue, checkGA4Status } from '../utils/errorTracking';

const GA4ErrorDashboard = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [ga4Status, setGa4Status] = useState('checking');
  const [errorCount, setErrorCount] = useState(0);
  const [warningCount, setWarningCount] = useState(0);
  const [performanceCount, setPerformanceCount] = useState(0);
  const [lastError, setLastError] = useState(null);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    checkGA4StatusAndUpdate();
  }, []);

  const checkGA4StatusAndUpdate = () => {
    const status = checkGA4Status();
    setGa4Status(status ? 'available' : 'unavailable');
  };

  const addLog = (message, type = 'info') => {
    const newLog = {
      message,
      type,
      timestamp: new Date().toLocaleTimeString()
    };
    setLogs(prev => [newLog, ...prev.slice(0, 9)]); // Mantener solo los últimos 10 logs
  };

  const testErrorTracking = () => {
    addLog('🧪 Testing error tracking...', 'test');
    
    try {
      // Test 1: Error simple
      const testError = new Error('Test error for GA4 dashboard');
      trackError(testError, {
        test: 'dashboard_test',
        component: 'GA4ErrorDashboard',
        timestamp: new Date().toISOString()
      });
      setErrorCount(prev => prev + 1);
      setLastError({
        message: testError.message,
        timestamp: new Date().toISOString(),
        type: 'test_error'
      });
      addLog('✅ Test error sent successfully', 'success');
      
    } catch (error) {
      addLog(`❌ Test failed: ${error.message}`, 'error');
    }
  };

  const testWarningTracking = () => {
    addLog('⚠️ Testing warning tracking...', 'test');
    
    try {
      trackWarning('Test warning for GA4 dashboard', {
        test: 'dashboard_test',
        severity: 'low'
      });
      setWarningCount(prev => prev + 1);
      addLog('✅ Test warning sent successfully', 'success');
      
    } catch (error) {
      addLog(`❌ Test failed: ${error.message}`, 'error');
    }
  };

  const testPerformanceTracking = () => {
    addLog('🐌 Testing performance tracking...', 'test');
    
    try {
      trackPerformanceIssue('Test performance issue', {
        test: 'dashboard_test',
        loadTime: performance.now()
      });
      setPerformanceCount(prev => prev + 1);
      addLog('✅ Test performance issue sent successfully', 'success');
      
    } catch (error) {
      addLog(`❌ Test failed: ${error.message}`, 'error');
    }
  };

  const clearCounters = () => {
    setErrorCount(0);
    setWarningCount(0);
    setPerformanceCount(0);
    setLastError(null);
    addLog('🧹 Counters cleared', 'info');
  };

  const clearLogs = () => {
    setLogs([]);
  };

  // Solo mostrar en desarrollo
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <>
      {/* Botón flotante */}
      <button
        onClick={() => setIsVisible(!isVisible)}
        className={`fixed bottom-4 right-4 z-50 rounded-full w-12 h-12 flex items-center justify-center shadow-lg transition-colors ${
          ga4Status === 'available' ? 'bg-green-500 hover:bg-green-600' :
          ga4Status === 'unavailable' ? 'bg-red-500 hover:bg-red-600' :
          'bg-yellow-500 hover:bg-yellow-600'
        } text-white`}
        title="GA4 Error Dashboard"
      >
        {ga4Status === 'available' ? '📊' : ga4Status === 'unavailable' ? '❌' : '⏳'}
      </button>

      {/* Panel del dashboard */}
      {isVisible && (
        <div className="fixed bottom-20 right-4 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 w-96 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">GA4 Error Dashboard</h3>
            <button
              onClick={() => setIsVisible(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            {/* Estado de GA4 */}
            <div className={`p-3 rounded-lg ${
              ga4Status === 'available' ? 'bg-green-50 border border-green-200' :
              ga4Status === 'unavailable' ? 'bg-red-50 border border-red-200' :
              'bg-yellow-50 border border-yellow-200'
            }`}>
              <div className="flex items-center justify-between">
                <span className={`font-medium ${
                  ga4Status === 'available' ? 'text-green-800' :
                  ga4Status === 'unavailable' ? 'text-red-800' :
                  'text-yellow-800'
                }`}>
                  {ga4Status === 'available' ? 'GA4 Disponible' :
                   ga4Status === 'unavailable' ? 'GA4 No Disponible' :
                   'Verificando...'}
                </span>
                <button
                  onClick={checkGA4StatusAndUpdate}
                  className="text-xs px-2 py-1 rounded bg-gray-200 hover:bg-gray-300"
                >
                  🔄
                </button>
              </div>
            </div>

            {/* Contadores */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-red-50 p-3 rounded-lg text-center">
                <div className="text-xl font-bold text-red-600">{errorCount}</div>
                <div className="text-xs text-red-600">Errores</div>
              </div>
              <div className="bg-yellow-50 p-3 rounded-lg text-center">
                <div className="text-xl font-bold text-yellow-600">{warningCount}</div>
                <div className="text-xs text-yellow-600">Warnings</div>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-center">
                <div className="text-xl font-bold text-blue-600">{performanceCount}</div>
                <div className="text-xs text-blue-600">Performance</div>
              </div>
            </div>

            {/* Último error */}
            {lastError && (
              <div className="bg-gray-50 p-3 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Último Error</h4>
                <div className="text-sm text-gray-700">
                  <div><strong>Mensaje:</strong> {lastError.message}</div>
                  <div><strong>Timestamp:</strong> {new Date(lastError.timestamp).toLocaleTimeString()}</div>
                  <div><strong>Tipo:</strong> {lastError.type}</div>
                </div>
              </div>
            )}

            {/* Botones de testing */}
            <div className="space-y-2">
              <button
                onClick={testErrorTracking}
                className="w-full bg-red-500 text-white py-2 px-4 rounded-md hover:bg-red-600 transition-colors text-sm"
              >
                Test Error
              </button>
              
              <button
                onClick={testWarningTracking}
                className="w-full bg-yellow-500 text-white py-2 px-4 rounded-md hover:bg-yellow-600 transition-colors text-sm"
              >
                Test Warning
              </button>
              
              <button
                onClick={testPerformanceTracking}
                className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 transition-colors text-sm"
              >
                Test Performance
              </button>
            </div>

            {/* Acciones */}
            <div className="flex space-x-2">
              <button
                onClick={clearCounters}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors text-sm"
              >
                Limpiar Contadores
              </button>
              
              <button
                onClick={clearLogs}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors text-sm"
              >
                Limpiar Logs
              </button>
            </div>

            {/* Logs */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Logs</h4>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {logs.length === 0 ? (
                  <p className="text-sm text-gray-500">No hay logs aún</p>
                ) : (
                  logs.map((log, index) => (
                    <div key={index} className={`text-xs font-mono ${
                      log.type === 'success' ? 'text-green-600' :
                      log.type === 'error' ? 'text-red-600' :
                      log.type === 'test' ? 'text-blue-600' :
                      'text-gray-600'
                    }`}>
                      <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Información */}
            <div className="text-xs text-gray-500 text-center">
              Solo visible en desarrollo • GA4 Dashboard
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GA4ErrorDashboard;
